export const getHoustonTime = (): string => {
    const date = new Date();
    return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).replace(/(\d+)\/(\d+)\/(\d+), /, '$3-$1-$2 ');
};

export const getHoustonDateInfo = () => {
    const now = new Date();
    const houstonToday = new Date(now.toLocaleString("en-US", {timeZone: "America/Chicago"}));
    houstonToday.setHours(0, 0, 0, 0);
    
    const houstonYesterday = new Date(houstonToday);
    houstonYesterday.setDate(houstonYesterday.getDate() - 1);

    const houstonTomorrow = new Date(houstonToday);
    houstonTomorrow.setDate(houstonTomorrow.getDate() + 1);
    
    return {
        today: houstonToday,
        yesterday: houstonYesterday,
        tomorrow: houstonTomorrow,
        todayString: houstonToday.toISOString().split('T')[0],
        yesterdayString: houstonYesterday.toISOString().split('T')[0],
        tomorrowString: houstonTomorrow.toISOString().split('T')[0],
        currentMonthNumber: houstonToday.getMonth() + 1,
        currentMonthName: houstonToday.toLocaleString("en-US", { month: "long" }),
        currentYearNumber: houstonToday.getFullYear(),
        currentYear: String(houstonToday.getFullYear()),
        currentQuarter: Math.ceil((houstonToday.getMonth() + 1) / 3)
    };
};

export const generateMonthYearOptions = () => {
    const options = [];
    const start = new Date(2020, 10); // Nov 2020
    const end = getHoustonDateInfo().today;
    end.setMonth(end.getMonth() + 2);
    
    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
        const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        options.push({ value: label, label });
    }
    
    return options.reverse();;
};

/**
 * IDs of producers that are active (is_active = 1 / true). Reuse this wherever a
 * dropdown must hide options belonging to a deactivated producer — e.g. leases,
 * which can stay active under a producer that was turned off.
 */
export const getActiveProducerIds = (producerDefinition?: any[]): Set<number> =>
    new Set(
        (producerDefinition ?? [])
            .filter((producer: any) => producer.is_active === 1 || producer.is_active === true)
            .map((producer: any) => Number(producer.id))
    );

export const getUniqueDropdownOptions = ({
    tableData,
    valueColumn,
    labelColumn,
    filterBy,
    isJsonColumn = false,
    includeNullOption = false,
    extraFields = []
}: {
    tableData?: any[];
    valueColumn?: string;
    labelColumn?: string;
    filterBy?: { column: string; value: any };
    isJsonColumn?: boolean;
    includeNullOption?: boolean;
    extraFields?: string[];
}) => {

    // Return empty array if no data
    if (!tableData || !tableData.length || !valueColumn) {
        return [];
    }

    // If no label column specified, use value column for both
    const labelCol = labelColumn || valueColumn;

    // Handle filtering
    let filteredData = tableData;
    if (filterBy) {
        filteredData = tableData.filter(row => row[filterBy.column] === filterBy.value);
    }

    // Create a map to track unique combinations
    const uniqueOptions = new Map<string, string>();

    filteredData.forEach(row => {
        const value = row[valueColumn];
        const label = row[labelCol];
        
        // Filter out inactive items if 'active' column exists
        if ('is_active' in row) {
            const isActive = row['is_active'];
            // Skip if active is 0, false, or falsy
            if (!isActive || isActive === 0 || isActive === false) {
                return;
            }
        }

        // Filter out soft-deleted items
        if ('created_in_error' in row && row['created_in_error']) {
            return;
        }
        
        // Filter out empty values
        if (value !== null && value !== undefined && value !== '') {
            if (isJsonColumn) {
                // Handle JSON column with label:value structure
                try {
                    let jsonArray;
                    
                    if (typeof value === 'string') {
                        // Clean the string and attempt to parse
                        const cleanedValue = value.trim();
                        
                        // Skip if empty after trimming
                        if (!cleanedValue) return;
                        
                        // Check if it looks like JSON
                        if (!cleanedValue.startsWith('[') && !cleanedValue.startsWith('{')) {
                            console.warn('JSON column value does not start with [ or {:', cleanedValue);
                            return;
                        }
                        
                        jsonArray = JSON.parse(cleanedValue);
                    } else if (Array.isArray(value)) {
                        jsonArray = value;
                    } else {
                        console.warn('Unexpected JSON column value type:', typeof value, value);
                        return;
                    }
                    
                    if (Array.isArray(jsonArray)) {
                        jsonArray.forEach(item => {
                            if (typeof item === 'object' && item !== null) {
                                // Handle objects with label:value structure
                                if (item.value !== undefined) {
                                    const itemLabel = item.label || item.name || String(item.value);
                                    const itemValue = String(item.value);
                                    
                                    if (itemValue !== '') {
                                        uniqueOptions.set(itemValue, itemLabel);
                                    }
                                } 
                                // Handle objects with single property
                                else {
                                    const keys = Object.keys(item);
                                    if (keys.length > 0) {
                                        const value = String(item[keys[0]] || '');
                                        if (value !== '') {
                                            uniqueOptions.set(value, value);
                                        }
                                    }
                                }
                            }
                        });
                    }
                } catch (error) {
                    console.error('Error parsing JSON column:', error, 'Value:', value);
                }
            } else {
                uniqueOptions.set(String(value), String(label));
            }
        }
    });

    // Convert map to dropdown options
    const dropdownOptions = Array.from(uniqueOptions.entries()).map(([value, label]) => {
        // Find the original row to get extra fields
        const originalRow = filteredData.find(row => String(row[valueColumn!]) === value);
        
        const option: any = { value, label };
        
        // Add extra fields if specified
        if (extraFields && originalRow) {
            extraFields.forEach(field => {
                option[field] = originalRow[field];
            });
        }
        
        return option;
    });

    // Sort options alphabetically
    dropdownOptions.sort((a, b) => {
        return a.label.localeCompare(b.label, undefined, { numeric: true });
    });

    // Add blank option at the top if requested
    if (includeNullOption) {
        dropdownOptions.unshift({ value: '', label: '(None)' });
    }

    return dropdownOptions;
};

export const updateJsonArray = (
    currentJsonValue: string | any[] | null | undefined,
    operation: 'add' | 'remove',
    valueToModify: any,
): string => {
    // Parse the current JSON value
    let jsonArray: any[] = [];
    
    try {
        if (typeof currentJsonValue === 'string') {
            jsonArray = JSON.parse(currentJsonValue);
        } else if (Array.isArray(currentJsonValue)) {
            jsonArray = [...currentJsonValue];
        } else if (currentJsonValue === null || currentJsonValue === undefined) {
            jsonArray = [];
        } else {
            // If it's an object, wrap it in an array
            jsonArray = [currentJsonValue];
        }
    } catch (error) {
        console.error('Error parsing JSON array:', error);
        jsonArray = [];
    }

    // Ensure we have an array
    if (!Array.isArray(jsonArray)) {
        jsonArray = [];
    }

    // Perform the operation
    if (operation === 'add') {
        // Add the new value to the array
        jsonArray.push(valueToModify);
    } else if (operation === 'remove') {
        // Remove matching values from the array
        jsonArray = jsonArray.filter(item => {
            // Deep comparison for objects, simple comparison for primitives
            if (typeof item === 'object' && typeof valueToModify === 'object') {
                return JSON.stringify(item) !== JSON.stringify(valueToModify);
            }
            return item !== valueToModify;
        });
    }

    // Return as JSON string
    return JSON.stringify(jsonArray);
};

// Helper function to filter object to only include original fields
export const filterArrayToOriginalFields = (
    jsonString: string,
    originalFields: string[]
): string => {
    try {
        const jsonArray = JSON.parse(jsonString);
        
        if (!Array.isArray(jsonArray)) {
            return jsonString; // Return as-is if not an array
        }
        
        // Filter each object in the array to only include original fields
        const filteredArray = jsonArray.map(item => {
            const filteredItem: any = {};
            originalFields.forEach(field => {
                if (item.hasOwnProperty(field)) {
                    filteredItem[field] = item[field];
                }
            });
            return filteredItem;
        });
        
        return JSON.stringify(filteredArray);
    } catch (error) {
        console.error('Error filtering array to original fields:', error);
        return jsonString;
    }
};

export const formatDateToYMD = (dateString: string) => {
    // Handle the GMT format: "Mon, 01 Sep 2025 00:00:00 GMT"
    const date = new Date(dateString);
    
    // Get year, month, day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
}

export const isMacOS = (): boolean => {
    // Try modern API first
    if ('userAgentData' in navigator && (navigator as any).userAgentData) {
        return (navigator as any).userAgentData.platform === 'macOS';
    }
    
    // Fallback to userAgent string
    return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};

export const getModifierKey = (e: React.MouseEvent | React.KeyboardEvent | KeyboardEvent | MouseEvent): boolean => {
    return isMacOS() ? e.metaKey : e.ctrlKey;
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

export const convertMonth = (value: number | string): string => {
    if (typeof value === 'number') {
        // Number → Month name
        if (value >= 1 && value <= 12) return MONTH_NAMES[value - 1];
        return '';
    }
    // Month name → Number (as string, e.g. "3")
    const index = MONTH_NAMES.findIndex(m => m.toLowerCase() === value.toLowerCase());
    return index >= 0 ? String(index + 1) : '';
};

/** Returns true when two carrier names refer to the same company.
 * Handles nicknames by checking whether either name is a substring
 * of the other (case-insensitive). e.g. "Fuego" matches "Fuego Transport LLC".
 */
export const carriersMatch = (
    a: string | null | undefined,
    b: string | null | undefined
): boolean => {
    if (!a || !b) return false;
    const na = a.toLowerCase();
    const nb = b.toLowerCase();
    return na === nb || na.includes(nb) || nb.includes(na);
};